import React from 'react';
import { COMMON_INPUT_LAYOUT_DEFAULT_PROPS, getCommonInputProps } from './BaseInputLayout';
import { DefaultDropdownLayoutProps, DefaultDropdownLayoutType, Option } from 'framework/form';
import ReactSelect, { mergeStyles, MultiValue, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { IndicatorsContainer } from './DropdownComponents';
import getDropdownLayoutCommonProps from './dropdownLayoutHelper';
import { InputLoader } from 'framework/ui/mui-builder';
import { useUiTheme } from 'framework/uiTheme';
import { getDropdownValue, hasValue } from './utils';
import { useTheme } from '@mui/material/styles';

interface FloatingLabelDropdownLayoutProps extends DefaultDropdownLayoutProps {}

const FLDropdownLayout: DefaultDropdownLayoutType = ({
  options,
  multiple,
  source,
  filterOption,
  formatOptionLabel,
  styles: customStyles,
  isLoading,
  isCreatable,
  onCreateOption,
  formatCreateLabel,
  getOptionValue,
  isClearable = true,
  sx,
  ...defaultLayoutProps
}: FloatingLabelDropdownLayoutProps) => {
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    source,
    ...defaultLayoutProps,
  };
  const { BaseInputLayout } = useUiTheme();
  const theme = useTheme();
  const inputProps = getCommonInputProps(props);
  const { onChange, onBlur = () => {}, value } = inputProps;

  const { selectClassName, classNamePrefix, styles } = getDropdownLayoutCommonProps(props, theme);
  const mergedStyles = mergeStyles(styles as any, customStyles || {});

  const selectOptions = options || [];
  const { disabled } = props;

  const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

  return (
    <BaseInputLayout
      {...props}
      value={hasValue(value)}
      className={`${props.className || ''} floating-label-dropdown`}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          transition: 'padding 0.3s ease',
          position: 'relative',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <SelectComponent
        {...inputProps}
        onCreateOption={onCreateOption}
        formatCreateLabel={formatCreateLabel}
        isLoading={isLoading}
        loadingMessage={() => <InputLoader />}
        value={getDropdownValue(multiple || false, value || '', options || [], getOptionValue)}
        className={selectClassName}
        classNamePrefix={classNamePrefix}
        isMulti={multiple}
        onBlur={onBlur}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        onChange={(newValue: SingleValue<Option> | MultiValue<Option>) => {
          if (multiple) {
            const selectedOptions = (newValue as readonly Option[]) || [];

            onChange({
              target: {
                options: selectedOptions.map(item => ({ ...item, selected: true })),
              },
            });
          } else {
            onChange({
              target: {
                value: newValue ? (newValue as Option)?.value : '',
              },
            });
          }
        }}
        isSearchable
        options={selectOptions}
        isClearable={isClearable}
        id={`field-dropdown-${source}`}
        components={{
          IndicatorsContainer,
          ...props?.components,
        }}
        isDisabled={disabled}
        isOptionDisabled={(option: Option) => !!option.disabled}
        filterOption={filterOption}
        formatOptionLabel={formatOptionLabel}
        getOptionValue={getOptionValue}
        styles={mergedStyles as any}
        placeholder={defaultLayoutProps.placeholderOnly ? defaultLayoutProps.placeholder : ''}
      />
    </BaseInputLayout>
  );
};

export default FLDropdownLayout;
